import { ReactNode } from "react";

import {
  createTheme,
  lighten,
  ThemeProvider as MUIThemeProvider
} from "@material-ui/core";
import { AlertClassKey } from "@material-ui/lab";

const theme = createTheme({
  // Breakpoints
  breakpoints: {
    values: {
      xs: 0,
      sm: 768,
      md: 992,
      lg: 1280,
      xl: 1920
    }
  },
  // Colors
  palette: {
    common: {
      black: "#151921"
    },
    type: "light",
    primary: {
      main: "#5c4ee5"
    },
    secondary: {
      main: "#FCC100",
      contrastText: "#151921"
    },
    error: {
      main: "#fa5252"
    },
    warning: {
      main: "#fab005"
    },
    success: {
      main: "#40c057"
    },
    info: {
      main: "#228be6"
    },
    text: {
      primary: "#151921",
      secondary: "#676b73",
      disabled: "#C5C5D2"
    },
    background: {
      default: "#f7f7f7"
    }
  },

  // Typography
  typography: {
    fontFamily: "Albert Sans, sans-serif",

    h1: {
      fontWeight: 500
    },
    h2: {
      fontWeight: 500
    },
    h3: {
      fontWeight: 500
    },
    h4: {
      fontWeight: 500
    },
    h5: {
      fontWeight: 500
    },
    h6: {
      fontWeight: 700
    },
    subtitle1: {
      fontWeight: 700
    },
    subtitle2: {
      fontWeight: 700
    },
    body1: {
      fontWeight: 500
    },
    body2: {
      fontWeight: 500
    },
    caption: {
      fontSize: "12px",
      fontWeight: 500
    },
    button: {
      fontSize: "14px",
      fontWeight: 500,
      textTransform: "capitalize"
    }
  },

  zIndex: {
    appBar: 198,
    drawer: 200,
    modal: 200,
    snackbar: 300,
    tooltip: 400
  },

  // Overrides
  overrides: {
    MuiCssBaseline: {
      "@global": {
        "*": {
          "&::-webkit-scrollbar": {
            width: 5,
            height: 5,
            backgroundColor: "transparent !important"
          },
          "&::-webkit-scrollbar-track": {
            backgroundColor: "transparent !important"
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "transparent !important",
            borderRadius: 5
          },
          "&:hover::-webkit-scrollbar-track": {
            backgroundColor: "#fafafa !important"
          },
          "&:hover::-webkit-scrollbar-thumb": {
            backgroundColor: "#ccc !important"
          }
        },
        body: {
          backgroundColor: "#f7f7f7 !important"
        }
      }
    },
    // Button
    MuiButton: {
      sizeSmall: {
        fontSize: "13px",
        fontWeight: 500,
        height: 30
      },
      iconSizeSmall: {
        fontSize: "16px"
      },
      iconSizeMedium: {
        fontSize: "17px"
      },
      iconSizeLarge: {
        fontSize: "18px"
      },
      sizeLarge: {
        fontSize: "15px",
        fontWeight: 500
      }
    },

    MuiAvatar: {
      circular: {
        fontSize: 14,
        fontWeight: 600
      }
    },

    MuiIconButton: {
      root: {
        height: 32,
        width: 32,
        padding: 0,
        color: "#9e9e9e"
      }
    },

    MuiSwitch: {
      root: {
        "& .MuiSwitch-colorPrimary.Mui-disabled.Mui-checked": {
          color: lighten("#5c4ee5", 0.5)
        }
      },
      switchBase: {
        padding: 9,
        height: "auto",
        width: "auto"
      },
      sizeSmall: {
        switchBase: {
          padding: 4
        }
      }
    },

    MuiTableRow: {
      root: {
        backgroundColor: "#fff",
        "&$selected": {
          backgroundColor: "#e6ecfd"
        },
        "&$selected:hover": {
          backgroundColor: "#e6ecfd !important"
        }
      },
      hover: {
        "&:hover": {
          backgroundColor: "#f0f0f0 !important"
        }
      }
    },

    MuiTableCell: {
      paddingCheckbox: {
        padding: "0 0 0 14px"
      }
    },

    MuiTableSortLabel: {
      root: {
        "&:hover, &:focus": {
          color: "#5c4ee5"
        }
      },
      active: {
        color: "#5c4ee5 !important"
      }
    },

    MuiDialog: {
      paperScrollBody: {
        verticalAlign: "top"
      },
      paper: {
        overflowY: "initial",
        marginTop: 40
      }
    },

    MuiTooltip: {
      tooltip: {
        fontSize: 13
      }
    },

    MuiAlert: {
      root: {
        wordBreak: "break-word"
      }
    },

    MuiInputBase: {
      adornedStart: {
        "& > svg:first-child": {
          marginRight: 7,
          color: "#676b73"
        }
      },
      adornedEnd: {
        "& > svg:last-child": {
          marginLeft: 7,
          color: "#676b73"
        }
      }
    }
  },

  // Default props
  props: {
    MuiCheckbox: {
      color: "primary"
    },
    MuiTooltip: {
      enterTouchDelay: 0
    },
    MuiMenu: {
      transitionDuration: 0
    },
    MuiPopover: {
      transitionDuration: 0
    }
  }
});

export const ThemeProvider = ({ children }: { children: ReactNode }) => (
  <MUIThemeProvider theme={theme}>{children}</MUIThemeProvider>
);

declare module "@material-ui/core/styles/overrides" {
  export interface ComponentNameToClassKey {
    MuiAlert: AlertClassKey;
  }
}
